/**
 * @packageDocumentation
 * @category Util
 */

/** `custom` is the only member carrying dates the reader chose; every other
 *  member is relative to now, so its bounds are recomputed on read rather than
 *  stored and left to go stale.
 *
 *  `live` still carries bounds, and deliberately the same day-wide ones as
 *  `h24`: the surface behind it needs a window to fetch and a band to draw
 *  either way. What it additionally means is that the surface should tail new
 *  arrivals instead of holding still, which is the surface's job, not the
 *  range's. */
export type DateRangePreset = 'custom' | 'd7' | 'd30' | 'd90' | 'h24' | 'live' | 'm3' | 'm6' | 'm12' | 'today' | 'yesterday';

type SpanPreset = 'd7' | 'd30' | 'd90' | 'h24' | 'live';

type MonthPreset = 'm3' | 'm6' | 'm12';

export type DateRangeValue = {
  /** Inclusive lower bound, ISO-8601. */
  from: string;
  preset: DateRangePreset;
  /** Exclusive upper bound, ISO-8601. */
  to: string;
};

const HOUR_MS = 60 * 60 * 1000;

export const PRESET_HOURS: Record<SpanPreset, number> = { d7: 168, d30: 720, d90: 2160, h24: 24, live: 24 };

const PRESET_MONTHS: Record<MonthPreset, number> = { m3: 3, m6: 6, m12: 12 };

/** `live` leads: it is the one entry that is not a length of time, and a reader
 *  looking for it is looking for now rather than for a span. */
export const PRESET_ORDER: Array<Exclude<DateRangePreset, 'custom'>> = ['live', 'today', 'yesterday', 'h24', 'd7', 'd30', 'd90', 'm3', 'm6', 'm12'];

/** True when the range means "and keep up", so a surface knows to subscribe. */
export const isLive = (value: DateRangeValue): boolean => value.preset === 'live';

/* Days are UTC days, and every consumer must format them with `timeZone: 'UTC'`.
   The warehouse buckets by UTC day, so a picker working in the reader's own
   calendar hands back bounds that straddle those buckets: picking 15 to 18 from
   GMT+4 produced a band running 14 to 18, one day wider at the front than the
   range it was drawing, with the extra day labelled a date the reader never
   chose. A day is the warehouse's day or the two never line up. */
export const startOfDay = (date: Date): Date => new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));

export const addDays = (date: Date, count: number): Date => new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() + count));

export const addMonths = (date: Date, count: number): Date => new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + count, 1));

export const dayKey = (date: Date): string => date.toISOString().slice(0, 10);

export const isSameDay = (left: Date, right: Date): boolean => dayKey(left) === dayKey(right);

/** The six weeks a month is drawn on, including the adjacent months' days that
 *  fill the first and last rows. Always 42 cells, so the grid does not change
 *  height as the reader pages through months. */
export const monthGrid = (month: Date, weekStart: number): Date[] => {
  const first = new Date(Date.UTC(month.getUTCFullYear(), month.getUTCMonth(), 1));
  const lead = (first.getUTCDay() - weekStart + 7) % 7;
  const start = addDays(first, -lead);
  return Array.from({ length: 42 }, (_, index) => addDays(start, index));
};

/** The weekday a locale's week starts on, as a `Date.getDay()` index. */
export const weekStartFor = (locale: string): number => {
  try {
    const parsed = new Intl.Locale(locale);
    const getWeekInfo: unknown = Reflect.get(parsed, 'getWeekInfo');
    const info: unknown = typeof getWeekInfo === 'function' ? getWeekInfo.call(parsed) : undefined;
    // The spec numbers Monday 1 through Sunday 7; `Date.getDay()` numbers Sunday 0.
    return typeof info === 'object' && info !== null && 'firstDay' in info && typeof info.firstDay === 'number' ? info.firstDay % 7 : 1;
  } catch {
    return 1;
  }
};

export const monthsBefore = (now: Date, count: number): Date => {
  const month = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - count, 1));
  const length = new Date(Date.UTC(month.getUTCFullYear(), month.getUTCMonth() + 1, 0)).getUTCDate();
  return new Date(Date.UTC(month.getUTCFullYear(), month.getUTCMonth(), Math.min(now.getUTCDate(), length), now.getUTCHours(), now.getUTCMinutes(), now.getUTCSeconds(), now.getUTCMilliseconds()));
};

export const presetRange = (preset: Exclude<DateRangePreset, 'custom'>, now: Date): DateRangeValue => {
  if (preset === 'today') return { from: startOfDay(now).toISOString(), preset, to: now.toISOString() };
  if (preset === 'yesterday') return { from: addDays(startOfDay(now), -1).toISOString(), preset, to: startOfDay(now).toISOString() };
  if (preset === 'm3' || preset === 'm6' || preset === 'm12') return { from: monthsBefore(now, PRESET_MONTHS[preset]).toISOString(), preset, to: now.toISOString() };
  return { from: new Date(now.getTime() - PRESET_HOURS[preset] * HOUR_MS).toISOString(), preset, to: now.toISOString() };
};

/** A chosen span, from the first instant of the start day to the first instant
 *  of the day AFTER the end day: the upper bound is exclusive, so an inclusive
 *  end day only lands whole if the bound sits past it. */
export const customRange = (start: Date, end: Date): DateRangeValue => {
  const [first, last] = start.getTime() <= end.getTime() ? [start, end] : [end, start];
  return { from: startOfDay(first).toISOString(), preset: 'custom', to: addDays(startOfDay(last), 1).toISOString() };
};

export const clampDay = (day: Date, min: Date, max: Date): Date => {
  const floor = startOfDay(min);
  const ceiling = startOfDay(max);
  if (day.getTime() < floor.getTime()) return floor;
  return day.getTime() > ceiling.getTime() ? ceiling : day;
};

export const isSelectable = (day: Date, min: Date, max: Date): boolean => day.getTime() >= startOfDay(min).getTime() && day.getTime() <= startOfDay(max).getTime();

/** Where a day sits in the span being drawn, so the grid can paint the ends and
 *  the fill differently without each cell re-deriving the comparison. */
export const dayPosition = (day: Date, start: Date | null, end: Date | null): 'end' | 'inside' | 'outside' | 'start' => {
  if (!start) return 'outside';
  if (!end) return isSameDay(day, start) ? 'start' : 'outside';
  const [first, last] = start.getTime() <= end.getTime() ? [start, end] : [end, start];
  if (isSameDay(day, first)) return 'start';
  if (isSameDay(day, last)) return 'end';
  return day.getTime() > first.getTime() && day.getTime() < last.getTime() ? 'inside' : 'outside';
};
